import PhoneNoInput from '@website/components/forms/fields/PhoneNoInput';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import {
  GetHostUpdateHostProfileFieldsQuery,
  HostProfileInput,
} from '@website/types/graphqlOperations';
import React from 'react';
import { Col, Row, Form, InputGroup } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import MediaSelectorField from '@website/components/forms/fields/MediaSelectorField';
import urlRegexSafe from 'url-regex-safe';

export function stripTwitterURLFromProfile(twitterPage?: string): string {
  return twitterPage?.replace(/(^|^[^:]+:\/\/|[^.]+\.)twitter\.com\//, '');
}

export function stripFBURLFromPage(fbPage?: string): string {
  return fbPage?.replace(/(^|^[^:]+:\/\/|[^.]+\.)facebook\.com\//, '');
}

export function addHTTPProtocolToWebsite(website: string) {
  if (website.includes('http://') || website.includes('https://')) {
    return website;
  }
  return `http://${website}`;
}

export function addFBUrlToPage(facebookPage: string) {
  const fbDomainRegex = /(^|^[^:]+:\/\/|[^.]+\.)facebook\.com/;
  if (fbDomainRegex.test(facebookPage)) {
    return facebookPage;
  }
  return `https://facebook.com/${facebookPage}`;
}

export function addTwitterUrlToPage(twitterPage: string) {
  const twitterDomainRegex = /(^|^[^:]+:\/\/|[^.]+\.)twitter\.com/;
  if (twitterDomainRegex.test(twitterPage)) {
    return twitterPage;
  }
  return `https://twitter.com/${twitterPage}`;
}

interface HostProfileFormProps {
  onSubmit: (values: HostProfileInput) => void;
  isLoading?: boolean;
  values?: HostProfileFormData;
  hostID?: string;
}

type HostProfileFormData = Omit<HostProfileInput, 'logo'> & {
  logo?: GetHostUpdateHostProfileFieldsQuery['hostByURL']['logo'];
};

export function HostProfileForm({
  onSubmit,
  isLoading,
  values,
  hostID,
}: HostProfileFormProps) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setError,
    clearErrors,
  } = useForm<HostProfileFormData>({
    mode: 'onBlur',
    defaultValues: {
      ...values,
      facebookPage: stripFBURLFromPage(values?.facebookPage),
      twitterPage: stripTwitterURLFromProfile(values?.twitterPage),
      website: values?.website?.replace(/^https?:\/\//, ''),
    },
    shouldFocusError: true,
  });

  const submitHostProfileForm = (formData: HostProfileFormData) => {
    const { twitterPage, facebookPage, website } = formData;

    const fbPageUrl = facebookPage ? addFBUrlToPage(facebookPage) : undefined;
    const twitterPageUrl = twitterPage
      ? addTwitterUrlToPage(twitterPage)
      : undefined;
    const websiteUrl = website ? addHTTPProtocolToWebsite(website) : undefined;

    const phone = `+${formData.contact.phone.replace('+', '')}`;

    onSubmit({
      ...formData,
      ...(formData.logo && {
        logo: {
          id: formData.logo?.id,
        },
      }),
      facebookPage: fbPageUrl,
      twitterPage: twitterPageUrl,
      website: websiteUrl,
      contact: { ...formData.contact, phone },
    });
  };

  return (
    <>
      <h4 className="h4">About your Hosting Business</h4>
      <Form role="form" onSubmit={handleSubmit(submitHostProfileForm)}>
        <Form.Group controlId="name" className="mb-2">
          <Form.Label>What is the name of your company? *</Form.Label>
          <Form.Control
            type="text"
            required
            placeholder="Host name"
            {...register('name', { required: true })}
            isInvalid={!!errors.name}
          />
          <Form.Text className="text-muted">
            This is name that will be publicly listed.
          </Form.Text>
        </Form.Group>
        <Form.Group controlId="about" className="mb-2">
          <Form.Label>Company Description *</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            type="text"
            required
            maxLength={500}
            placeholder="About host"
            {...register('about', {
              required: true,
              // Prevent setting links or email addresses.
              validate: (value) => {
                const match = value.match(urlRegexSafe());
                return !match;
              },
            })}
            isInvalid={!!errors.about}
          />
          <Form.Text className="text-muted">
            A short (1-2 sentences) description of your company.
          </Form.Text>
          {errors.about?.type === 'validate' && (
            <Form.Control.Feedback type="invalid">
              You are not allowed to include links or email addresses here.
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group controlId="website" className="mb-2">
          <Form.Label>Website</Form.Label>
          <InputGroup>
            <InputGroup.Text>
              <span id="websiteProtocol">http://</span>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="example.com"
              isInvalid={!!errors.website}
              {...register('website', {
                setValueAs: (value) => value?.replace(/^https?:\/\//, ''),
              })}
            />
          </InputGroup>
        </Form.Group>
        <Form.Group controlId="facebookPage" className="mb-2">
          <Form.Label>Facebook Page</Form.Label>
          <InputGroup className="mb-3">
            <InputGroup.Text>
              <span id="facebookAddon">https://facebook.com/</span>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Facebook Page"
              isInvalid={!!errors.facebookPage}
              {...register('facebookPage', {
                setValueAs: (value: string) =>
                  value
                    ?.replace(/(^|^[^:]+:\/\/|[^.]+\.)twitter\.com\//, '')
                    // remove any spaces in the url
                    ?.replace(/ /g, ''),
              })}
            />
          </InputGroup>
        </Form.Group>

        {/* We need the host id in order associate an image with a host */}
        {hostID && (
          <Form.Group className="mb-2">
            <Form.Label>Logo</Form.Label>
            <MediaSelectorField control={control} hostID={hostID} name="logo" />
          </Form.Group>
        )}

        <Form.Group controlId="twitterPage" className="mb-2">
          <Form.Label>Twitter Page</Form.Label>
          <InputGroup className="mb-3">
            <InputGroup.Text>
              <span id="twitterAddon">https://twitter.com/</span>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Twitter Page"
              isInvalid={!!errors.twitterPage}
              {...register('twitterPage', {
                setValueAs: (value) =>
                  value
                    ?.replace(/(^|^[^:]+:\/\/|[^.]+\.)twitter\.com\//, '')
                    // remove any spaces in the url
                    ?.replace(/ /g, ''),
              })}
            />
          </InputGroup>
        </Form.Group>
        <h4 className="h4 mt-5">Contact Information</h4>
        <Form.Group controlId="contactName" className="mb-2">
          <Form.Label>Contact Name *</Form.Label>
          <Form.Control
            type="text"
            required
            placeholder="Contact Name"
            {...register('contact.contactName', { required: true })}
            isInvalid={!!errors.contact?.contactName}
          />
          <Form.Text className="text-muted">
            This name will not be public!
          </Form.Text>
        </Form.Group>
        <Row>
          <Col sm={12} md={6}>
            <Form.Group controlId="email" className="mb-2">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                required
                placeholder="Email"
                {...register('contact.email', { required: true })}
                isInvalid={!!errors.contact?.email}
              />
              <Form.Text className="text-muted">
                This email address will not be made public.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col sm={12} md={6}>
            <Form.Group className="mb-2">
              <Form.Label htmlFor="contactPhone">Phone *</Form.Label>
              <PhoneNoInput
                control={control}
                name="contact.phone"
                id="contactPhone"
                required
                setError={setError}
                clearErrors={clearErrors}
              />
              <Form.Text className="text-muted">
                Emergency phone number. This will be public.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <SubmitButton
          text="Submit"
          submittingLabel="Submitting"
          isSubmitting={isLoading}
        />
      </Form>
    </>
  );
}

HostProfileForm.defaultProps = {
  values: undefined,
  isLoading: false,
};
